import { create } from 'zustand';
import { markStarted, markPlayed, markSkipped, streamUrl } from '../api';
import { trackPlay, trackSkip } from '../analytics';

const usePlayerStore = create((set, get) => ({
  // Queue
  queue: [],
  currentIndex: -1,
  currentTrack: null,
  history: [], // last 2 played tracks

  // Playback state
  isPlaying: false,
  volume: 0.3,
  shuffle: false,
  repeat: 'none', // 'none' | 'one' | 'all'

  // Audio element ref (set by Player component)
  audioRef: null,
  setAudioRef: (ref) => set({ audioRef: ref }),

  // Load a queue and start playing
  playQueue: (tracks, startIndex = 0) => {
    trackPlay(tracks[startIndex]);
    markStarted(tracks[startIndex].id).catch(() => {});
    set({ queue: tracks, currentIndex: startIndex, currentTrack: tracks[startIndex], isPlaying: true, history: [] });
  },

  playTrack: (track) => {
    trackPlay(track);
    markStarted(track.id).catch(() => {});
    set({ queue: [track], currentIndex: 0, currentTrack: track, isPlaying: true, history: [] });
  },

  togglePlay: () => {
    const { isPlaying, audioRef } = get();
    if (audioRef?.current) {
      isPlaying ? audioRef.current.pause() : audioRef.current.play();
    }
    set({ isPlaying: !isPlaying });
  },

  next: (isSkip = true) => {
    const { queue, currentIndex, shuffle, repeat, currentTrack, history } = get();
    console.log('next called, currentIndex:', currentIndex, 'queue length:', queue.length, 'currentTrack:', currentTrack?.title);
    console.log('queue order:', queue.map(t => t.title));
    if (!queue.length) return;

    // Always find current track's true position in case queue was reordered
    const trueIndex = queue.findIndex(t => t.id === currentTrack?.id);
    const resolvedIndex = trueIndex !== -1 ? trueIndex : currentIndex;

    const newHistory = currentTrack
      ? [...history, currentTrack].slice(-2)
      : history;

    let nextIndex;
    if (shuffle) {
      nextIndex = Math.floor(Math.random() * queue.length);
    } else if (resolvedIndex < queue.length - 1) {
      nextIndex = resolvedIndex + 1;
    } else if (repeat === 'all') {
      nextIndex = 0;
    } else {
      set({ isPlaying: false, history: newHistory });
      return;
    }

    const next = queue[nextIndex];
    if (isSkip) {
      trackSkip(queue[resolvedIndex]);
      markSkipped(queue[resolvedIndex].id).catch(() => {});
    }
    trackPlay(next);
    markStarted(next.id).catch(() => {});
    set({ currentIndex: nextIndex, currentTrack: next, isPlaying: true, history: newHistory });
  },

  prev: () => {
    const { queue, currentIndex, audioRef, currentTrack, history } = get();
    // If more than 3s in, restart current track
    if (audioRef?.current && audioRef.current.currentTime > 3) {
      audioRef.current.currentTime = 0;
      return;
    }
    // Add current to history when going back
    const newHistory = currentTrack
      ? [...history, currentTrack].slice(-2)
      : history;

    if (currentIndex > 0) {
      const prevIndex = currentIndex - 1;
      set({ currentIndex: prevIndex, currentTrack: queue[prevIndex], isPlaying: true, history: newHistory });
    }
  },

  onTrackEnd: () => {
    const { currentTrack, repeat, history } = get();
    if (currentTrack) markPlayed(currentTrack.id).catch(() => {});

    if (repeat === 'one') {
      const { audioRef } = get();
      if (audioRef?.current) { audioRef.current.currentTime = 0; audioRef.current.play(); }
      return;
    }
    get().next(false); // auto-advance, not a skip
  },

  setVolume: (v) => {
    set({ volume: v });
    const { audioRef } = get();
    if (audioRef?.current) audioRef.current.volume = v;
  },

  toggleShuffle: () => set((s) => ({ shuffle: !s.shuffle })),
  toggleRepeat: () => set((s) => {
    const next = { none: 'one', one: 'all', all: 'none' };
    return { repeat: next[s.repeat] };
  }),

  getStreamUrl: (track) => streamUrl(track.id),

  // Remove a track from the queue by index (session only)
  removeFromQueue: (index) => {
    const { queue, currentIndex } = get();
    const newQueue = queue.filter((_, i) => i !== index);
    // Adjust currentIndex if needed
    let newIndex = currentIndex;
    if (index < currentIndex) newIndex = currentIndex - 1;
    else if (index === currentIndex) newIndex = Math.min(currentIndex, newQueue.length - 1);
    set({ queue: newQueue, currentIndex: newIndex, currentTrack: newQueue[newIndex] || null });
  },

  //drag and drop an upcoming track to a different play location
  reorderQueue: (fromIndex, toIndex) => {
    const { queue, currentIndex } = get();
    
    // Safety check — don't allow dragging current or history tracks
    if (fromIndex <= currentIndex || toIndex <= currentIndex) return;
    
    const newQueue = [...queue];
    const [moved] = newQueue.splice(fromIndex, 1);
    newQueue.splice(toIndex, 0, moved);

    // currentIndex never changes — we only reorder tracks AFTER current
    set({ queue: newQueue });
  },

  // Jump to a specific track in the queue
  jumpToTrack: (index) => {
    const { queue, currentTrack, history } = get();
    if (index < 0 || index >= queue.length) return;
    const newHistory = currentTrack
      ? [...history, currentTrack].slice(-2)
      : history;
    set({ currentIndex: index, currentTrack: queue[index], isPlaying: true, history: newHistory });
  },
}));

export default usePlayerStore;